/*
 * Copyright (C) 2023 - present Marek Kuzora - All Rights Reserved.
 */


import { Client } from 'fierry/flux';

import Now from 'shared/query/current.time';
import Url from 'shared/client/url';
import Galaxy from 'shared/query/galaxy';
import Navigation from 'shared/client/navigation';

import UnitEnum from 'astro/enum/unit';
import ShapeEnum from 'shared/enum/shape';

import * as Coords from 'shared/geo/coords';
import * as Shapes from 'shared/geo/shapes';


const isRangeTargetEntity = (type) =>
{
  return type === UnitEnum.Artillery || type === UnitEnum.AssaultTroops || type === UnitEnum.MechInfantry || type === UnitEnum.SpecialForces || type === UnitEnum.Gate;
}


const getOffensiveShape = (type) =>
{
  switch (type)
  {
    case UnitEnum.Artillery:     return ShapeEnum.QuadrupleArea;
    case UnitEnum.MechInfantry:  return ShapeEnum.TripleArea;
    case UnitEnum.AssaultTroops: return ShapeEnum.Single;
    case UnitEnum.SpecialForces: return ShapeEnum.Single;
    case UnitEnum.Gate:          return ShapeEnum.Single;
  }
}


export default new Client([ Navigation, Galaxy, Url, Now ], class
{
  constructor (navigation, galaxy, url, now)
  {
    this.galaxy = galaxy;
    this.now = now;

    this.source = navigation.getCurrent();
    this.target = navigation.getRawTarget();

    this.args = url.getRoute().getArgs();
  }


  onDone ()
  {
    if (isRangeTargetEntity(this.args.type))
    {
      const shape = this.__getTargetShape__();

      this.galaxy.createBuildingWithTarget(this.source, this.args.type, this.target, shape);
    }
    else
    {
      this.galaxy.createBuilding(this.source, this.args.type);
    }
  }


  getSelectionTargets ()
  {
    const mapCallback = (coords) =>
    {
      return this.galaxy.getLocation(coords);
    };

    return this.__getTargetShape__().data.map(mapCallback);
  }


  __getTargetShape__ ()
  {
    const downgradeCallback = (coords) =>
    {
      return this.galaxy.getLocation(coords).getZone() == null;
    };

    const targetShape = this.__getConstantsShape__(this.target.getCoords(), this.source.getCoords())[0];
    const downgrade = Shapes.getDowngrade(this.target.getCoords(), targetShape, downgradeCallback);

    return downgrade.data.length ? downgrade : { data: [ this.target.getCoords() ] };
  }


  __getConstantsShape__ (target, source)
  {
    return Shapes.getMaxVariants(target, source, getOffensiveShape(this.args.type));
  }


  getError ()
  {
    const type = this.args.type;
    const zone = this.__getZone__();

    if (type == null)
    {
      return 'No building type found';
    }

    /*if (!zone.getCass().isCenter())
    {
      return 'Building has to be constructed in the sector\'s center';
    }*/

    /*if (zone.getSector().getBuilding() != null)
    {
      return 'Sector can only have one building';
    }*/

    const constants = this.galaxy.getConstants().getEntity(type);

    if (zone.getAttributes().getInfluenceAdvantage() < constants.getAttrition())
    {
      return 'Not enough zone influence to construct entity';
    }

    const userResources = this.source.getUser().getResources(this.now); // TODO needs to use global user - this is tricky access...
    const buildingResources = constants.getResources();

    if (userResources.currency < buildingResources.currency || userResources.carbon < buildingResources.carbon || userResources.silicon < buildingResources.silicon || userResources.hydrogen < buildingResources.hydrogen)
    {
      return 'You don\'t have enough resources to construct';
    }

    /*if (zone.getPlanet().getEntities().getActiveConstruction(zone.getUserID()))
    {
      return 'One building construction / upgrade per planet';
    }*/

    if (type === 'spaceport' && zone.getSector().getNeighbors().getMaybeDocks().length === 0)
    {
      return 'Spaceport requires access to planet edge';
    }

    if (isRangeTargetEntity(type))
    {
      if (this.source.equals(this.target))
      {
        return 'Unit cannot attack zone it occupies.';
      }

      const distance  = Coords.getDistance(this.source.getCoords(), this.target.getCoords());

      if (distance > constants.getRange())
      {
        return 'Target zone is out of range. Please select a closer target.';
      }

      if (type !== UnitEnum.Gate && this.target.getZone() == null)
      {
        return 'Cannot target an empty zone';
      }

      if (type === UnitEnum.Gate)
      {
        for (const target of this.getSelectionTargets())
        {
          if (target.getPlatform() || target.getZone() || target.getSpaceship())
          {
            return 'Needs an empty zone to create platform.';
          }
        }
      }
    }

    return null;
  }


  getInitial (target)
  {
    if (this.args.type === '')
    {
      return 'Select building type';
    }

    if (isRangeTargetEntity(this.args.type))
    {
      if (this.target == null)
      {
        return 'Select target zone';
      }
    }

    return null;
  }


  __getZone__ ()
  {
    if (this.source.getPlatform())
    {
      const source = this.source.getPlatform().getAttributes().getPlatformSource();

      return this.galaxy.getLocation(source.id).getZone();
    }

    return this.source.getZone();
  }


});
